// SPDX-License-Identifier: AGPL-3.0-or-later
//
// parley: the message descriptors of the soundboard (strand AG), kept
// together the way the upstream tabs keep theirs at the top of the file.

import {msg} from '@lingui/core/macro';

export const SOUNDBOARD_DESCRIPTOR = msg({
	message: 'Soundboard',
	context: 'community-settings-tab',
	comment: 'Community settings tab and voice control label for the community soundboard.',
});
export const SOUNDBOARD_EMPTY_DESCRIPTOR = msg({
	message: 'This community has no sounds yet',
	comment: 'Empty state inside the soundboard panel above the voice control bar.',
});
export const SOUNDBOARD_PLAY_FAILED_DESCRIPTOR = msg({
	message: 'That sound could not be played',
	comment: 'Toast shown when playing a soundboard sound was rejected by the server.',
});
export const SOUNDBOARD_UPLOAD_DESCRIPTOR = msg({
	message: 'Upload sound',
	comment: 'Button in the community soundboard settings that opens the file picker.',
});
export const SOUNDBOARD_NO_SOUNDS_DESCRIPTOR = msg({
	message: 'No sounds yet.',
	comment: 'Empty state in the community soundboard settings.',
});
export const SOUNDBOARD_NAME_DESCRIPTOR = msg({
	message: 'Sound name',
	comment: 'Accessible label of the name field of a soundboard sound.',
});
export const SOUNDBOARD_VOLUME_DESCRIPTOR = msg({
	message: 'Volume',
	comment: 'Accessible label of the volume slider of a soundboard sound.',
});
export const SOUNDBOARD_EMOJI_DESCRIPTOR = msg({
	message: 'Emoji',
	comment: 'Accessible label and placeholder of the emoji field of a soundboard sound.',
});
export const SOUNDBOARD_PREVIEW_DESCRIPTOR = msg({
	message: 'Preview',
	comment: 'Button that plays a soundboard sound for the current user only.',
});
export const SOUNDBOARD_SAVE_DESCRIPTOR = msg({
	message: 'Save',
	comment: 'Button that saves the edited name, emoji and volume of a soundboard sound.',
});
export const SOUNDBOARD_DELETE_DESCRIPTOR = msg({
	message: 'Delete',
	comment: 'Button that deletes a soundboard sound.',
});
export const SOUNDBOARD_DELETE_TITLE_DESCRIPTOR = msg({
	message: 'Delete sound',
	comment: 'Title of the confirmation dialog for deleting a soundboard sound.',
});
export const SOUNDBOARD_DELETE_BODY_DESCRIPTOR = msg({
	message: '{soundName} will be removed from this community’s soundboard.',
	comment:
		'Body of the confirmation dialog for deleting a soundboard sound. {soundName} is the name of the sound. Tone stays plain.',
});
export const SOUNDBOARD_UNAVAILABLE_DESCRIPTOR = msg({
	message: 'unavailable',
	comment:
		'Marker next to a soundboard sound that is over the configured limit and therefore cannot be played. Lowercase, shown after a separator.',
});
export const SOUNDBOARD_SAVE_FAILED_BODY_DESCRIPTOR = msg({
	message: 'Check the name and try again.',
	comment: 'Body of the error dialog when editing a soundboard sound failed.',
});
export const SOUNDBOARD_UPLOAD_FAILED_BODY_DESCRIPTOR = msg({
	message: 'It must be an MP3, Ogg, M4A or WAV file of at most {maxSeconds} seconds.',
	comment: 'Body of the upload error dialog. {maxSeconds} is the configured maximum length in seconds.',
});

// --- The panel above the voice bar (strand AJ) ---

export const SOUNDBOARD_SEARCH_DESCRIPTOR = msg({
	message: 'Find the perfect sound',
	comment: 'Placeholder of the search field at the top of the soundboard panel.',
});
export const SOUNDBOARD_STOP_DESCRIPTOR = msg({
	message: 'Stop playing sounds',
	comment: 'Button next to the soundboard search field that silences the sounds currently playing for you.',
});
export const SOUNDBOARD_PANEL_VOLUME_DESCRIPTOR = msg({
	message: 'Soundboard volume',
	comment: 'Button and slider at the top of the soundboard panel that set how loud sounds are played for you.',
});
export const SOUNDBOARD_FAVORITES_DESCRIPTOR = msg({
	message: 'Favorites',
	comment: 'Section heading and jump mark in the soundboard panel for the sounds the user marked.',
});
export const SOUNDBOARD_RECENT_DESCRIPTOR = msg({
	message: 'Frequently used',
	comment: 'Section heading and jump mark in the soundboard panel for the sounds this user plays most.',
});
export const SOUNDBOARD_GUILD_SECTION_DESCRIPTOR = msg({
	message: 'Sounds in {guildName}',
	comment: 'Section heading in the soundboard panel. {guildName} is the name of the community.',
});
export const SOUNDBOARD_ADD_FAVORITE_DESCRIPTOR = msg({
	message: 'Add to favorites',
	comment: 'Button on a soundboard tile that marks the sound as a favorite.',
});
export const SOUNDBOARD_REMOVE_FAVORITE_DESCRIPTOR = msg({
	message: 'Remove from favorites',
	comment: 'Button on a soundboard tile that removes the sound from the favorites.',
});
export const SOUNDBOARD_NO_RESULTS_DESCRIPTOR = msg({
	message: 'No sound matches that search',
	comment: 'Empty state in the soundboard panel while a search is active.',
});
export const SOUNDBOARD_EMPTY_HINT_DESCRIPTOR = msg({
	message: 'Anyone who may manage expressions can upload one in the community settings.',
	comment: 'Hint under the empty state of the soundboard panel, pointing at the community settings page.',
});
export const SOUNDBOARD_ONLY_THIS_COMMUNITY_DESCRIPTOR = msg({
	message: 'Only sounds from this community can be played here.',
	comment:
		'Footnote in the soundboard panel explaining why no other communities are listed. Tone stays plain and factual.',
});
export const SOUNDBOARD_COLLAPSE_SECTION_DESCRIPTOR = msg({
	message: 'Collapse section',
	comment: 'Accessible label of the caret button that folds a section of the soundboard panel away.',
});
export const SOUNDBOARD_EXPAND_SECTION_DESCRIPTOR = msg({
	message: 'Expand section',
	comment: 'Accessible label of the caret button that unfolds a section of the soundboard panel.',
});

// --- Trimming an upload inside the upload dialog (strand AJ) ---

export const SOUNDBOARD_TRIM_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Drag the handles to pick the part you want. A soundboard sound may be at most {maxSeconds} seconds long.',
	comment: 'Description in the trim dialog. {maxSeconds} is the configured maximum length in seconds.',
});
export const SOUNDBOARD_TRIM_START_DESCRIPTOR = msg({
	message: 'Start of the section',
	comment: 'Accessible label of the left handle in the trim dialog.',
});
export const SOUNDBOARD_TRIM_END_DESCRIPTOR = msg({
	message: 'End of the section',
	comment: 'Accessible label of the right handle in the trim dialog.',
});
export const SOUNDBOARD_TRIM_LENGTH_DESCRIPTOR = msg({
	message: '{selected}s of {max}s',
	comment: 'Live length readout in the trim dialog. {selected} and {max} are lengths in seconds, one decimal each.',
});
export const SOUNDBOARD_TRIM_PLAY_DESCRIPTOR = msg({
	message: 'Preview section',
	comment: 'Button in the trim dialog that plays the selected section.',
});
export const SOUNDBOARD_TRIM_STOP_DESCRIPTOR = msg({
	message: 'Stop preview',
	comment: 'Button in the trim dialog that stops the preview.',
});
export const SOUNDBOARD_TRIM_WAVEFORM_DESCRIPTOR = msg({
	message: 'Waveform of the chosen file',
	comment: 'Accessible description of the waveform drawing in the trim dialog.',
});
export const SOUNDBOARD_TRIMMED_TOO_LARGE_DESCRIPTOR = msg({
	message: 'The section is still over {maxSize}. Choose a shorter section.',
	comment: 'Error text when even the trimmed section exceeds the size limit. {maxSize} is a formatted file size.',
});

// --- The upload dialog (strand AJ) ---

export const SOUNDBOARD_UPLOAD_TITLE_DESCRIPTOR = msg({
	message: 'Upload sound',
	context: 'soundboard-upload-dialog',
	comment: 'Title of the dialog that uploads a sound to the community soundboard.',
});
export const SOUNDBOARD_FILE_DESCRIPTOR = msg({
	message: 'Sound file',
	comment: 'Label of the file row in the soundboard upload dialog.',
});
export const SOUNDBOARD_FILE_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'No file chosen',
	comment: 'Shown in the file row of the soundboard upload dialog while no file has been picked.',
});
export const SOUNDBOARD_UPLOAD_BROWSE_DESCRIPTOR = msg({
	message: 'Browse',
	comment: 'Button in the soundboard upload dialog that opens the file picker.',
});
export const SOUNDBOARD_UPLOAD_CONFIRM_DESCRIPTOR = msg({
	message: 'Upload',
	comment: 'Primary button of the soundboard upload dialog.',
});
export const SOUNDBOARD_EMOJI_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'Pick an emoji',
	comment: 'Placeholder of the emoji field in the soundboard upload dialog.',
});
export const SOUNDBOARD_FILE_UNREADABLE_DESCRIPTOR = msg({
	message: 'That file could not be read. It must be an MP3, Ogg, M4A or WAV file this browser can play.',
	comment: 'Error text in the soundboard upload dialog when the browser cannot decode the chosen file.',
});
export const SOUNDBOARD_EDIT_TITLE_DESCRIPTOR = msg({
	message: 'Edit sound',
	comment: 'Title of the dialog that changes the name, emoji and volume of a soundboard sound.',
});
export const SOUNDBOARD_EMOJI_CLEAR_DESCRIPTOR = msg({
	message: 'Remove emoji',
	comment: 'Button next to the emoji field of the soundboard sound dialog that clears the chosen emoji.',
});

// --- The settings list (strand AJ) ---

export const SOUNDBOARD_EDIT_DESCRIPTOR = msg({
	message: 'Edit',
	comment: 'Button on a row of the community soundboard settings that opens the edit dialog of that sound.',
});
export const SOUNDBOARD_COLUMN_SOUND_DESCRIPTOR = msg({
	message: 'Sound',
	comment: 'Column heading in the community soundboard settings list.',
});
export const SOUNDBOARD_COLUMN_UPLOADER_DESCRIPTOR = msg({
	message: 'Uploaded by',
	comment: 'Column heading in the community soundboard settings list: who uploaded the sound.',
});
export const SOUNDBOARD_UNKNOWN_UPLOADER_DESCRIPTOR = msg({
	message: 'Unknown user',
	comment: 'Shown in the "Uploaded by" column when the uploader is not known to this client.',
});
